import { Injectable } from "@angular/core";
import { EMPTY, catchError, from, map, mergeMap, of, tap } from "rxjs";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as invoicesActions from "./invoices.actions";
import * as studentsActions from "../../students/ngrx/students.actions";
import * as appointmentsActions from "../../calendar/ngrx/calendar.actions";
import * as paymentsActions from "../payment-ngrx/payments.actions";
import * as errorActions from "../../core/error/ngrx/error.actions";
import { DataService } from "src/app/supabase/services/data.service";
import { Filesystem, FilesystemDirectory } from "@capacitor/filesystem";
import { ToastController } from "@ionic/angular";
import { FunctionService } from "src/app/supabase/services/function.service";

@Injectable()
export class InvoiceEffects {
  formattedDate: any;
    constructor(
        private actions$: Actions,
        private dataService: DataService,
        private functionService: FunctionService,
        private toastController: ToastController,
    ) {
      const today = new Date();
      this.formattedDate = today.toISOString().substr(0, 10);
    }

    loadInvoices$ = createEffect(() => this.actions$.pipe(
        ofType(invoicesActions.loadInvoices),
        mergeMap(() => 
          from(this.dataService.getInvoices()).pipe(
            mergeMap(invoices => [
              studentsActions.upsertStudents({students: invoices.map(invoice => invoice.student)}),
              appointmentsActions.upsertAppointments({appointments: invoices.map(invoice => invoice.appointment)}),
              invoicesActions.loadInvoicesSuccess({invoices: invoices.map(invoice => ({...invoice, student: undefined, appointment: undefined}))})
            ]),
            catchError((error) => of(errorActions.handleError({error: "Die Rechnungen konnten nicht geladen werden.", showToast: false, exception: error})))
          )
        )
      ));

    loadOpenInvoiceItems$ = createEffect(() => this.actions$.pipe(
        ofType(invoicesActions.getOpenInvoiceItems),
        mergeMap(({ student_id }) =>
          from(this.dataService.getOpenInvoiceItems(student_id)).pipe(
            map(invoiceItems => invoicesActions.getOpenInvoiceItemsSuccess({invoiceItems})),
            catchError((error) => of(errorActions.handleError({error: "Die offenen Rechnungspositionen konnten nicht geladen werden.", exception: error})))
          )
        )
      ));

    deleteInvoice$ = createEffect(() => this.actions$.pipe(
        ofType(invoicesActions.deleteInvoice),
        mergeMap(({invoiceId}) => 
          from(this.dataService.deleteInvoice(invoiceId)).pipe(
            map(() => invoicesActions.deleteInvoiceSuccess({invoiceId})),
          )
        )
      ));

    addInvoice$ = createEffect(() => this.actions$.pipe(
        ofType(invoicesActions.addInvoice),
        mergeMap(({invoice, status, file}) => 
          from(this.dataService.addInvoice(invoice, status, file)).pipe(
            mergeMap(invoice => [
              invoicesActions.addInvoiceSuccess({invoice}),
              paymentsActions.upsertPayments({payments: invoice.payments}),
            ]),
            catchError((error) => of(errorActions.handleError({error: "Die Rechnung konnte nicht erstellt werden.", exception: error})))
          )
        )
      ));

    downloadPdf$ = createEffect(() => this.actions$.pipe(
        ofType(invoicesActions.downloadPdf),
        mergeMap(({pdf, fileName}) => 
          from(Filesystem.writeFile({
            path: fileName,
            data: pdf,
            directory: FilesystemDirectory.Documents,
          })).pipe(
            map(base64 => invoicesActions.downloadPdfSuccess()),
            catchError(async () => invoicesActions.downloadPdfFailure())
          )
        )
      ));

    downloadPdfSuccess$ = createEffect(() => this.actions$.pipe(
      // show a toast message
      ofType(invoicesActions.downloadPdfSuccess),
      tap(async () => {
        const toast = await this.toastController.create({
          message: 'PDF wurde heruntergeladen',
          duration: 2000,
          color: 'success'
        });
        toast.present();
      })
    ), { dispatch: false });

    downloadPdfFailure$ = createEffect(() => this.actions$.pipe(
      // show a toast message
      ofType(invoicesActions.downloadPdfFailure),
      tap(async () => {
        const toast = await this.toastController.create({
          message: 'PDF konnte nicht heruntergeladen werden',
          duration: 2000,
          color: 'danger'
        });
        toast.present();
      })
    ), { dispatch: false });

    sendMail$ = createEffect(() => this.actions$.pipe(
      ofType(invoicesActions.sendMail),
      mergeMap(({sendRequest, payOnline}) => 
        from(this.functionService.sendMail(sendRequest, payOnline)).pipe(
          map(() => invoicesActions.sendMailSuccess()),
        )
      )
    ));

    createAndSendInvoiceMail$ = createEffect(() => this.actions$.pipe(
      ofType(invoicesActions.createAndSendInvoiceMail),
      mergeMap(({sendRequest, invoiceAddRequest, file, payOnline}) => 
        from(this.dataService.addInvoice(invoiceAddRequest, "", file)).pipe(
          tap(newInvoice => {
            sendRequest = { ...sendRequest, invoiceId: newInvoice.invoice_id };
          }),
          mergeMap(newInvoice => [
            invoicesActions.sendMail({sendRequest, payOnline}),
            invoicesActions.addInvoiceSuccess({invoice: newInvoice}),
            paymentsActions.upsertPayments({payments: newInvoice.payments})
          ]),
        )
      )
    ));

    addInvoiceStatus$ = createEffect(() => this.actions$.pipe(
      ofType(invoicesActions.addInvoiceStatus),
      mergeMap(({invoiceId, status}) => 
        from(this.dataService.addInvoiceStatus(invoiceId, status)).pipe(
          map((status) => invoicesActions.addInvoiceStatusSuccess({status})),
        )
      )
    ))
            
}
